import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { adminAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/helpers';
import { Plus, Trophy, Trash2, CheckCircle2, Lock } from 'lucide-react';

const emptyForm = { title: '', description: '', category: 'general', currency: '', min_bet: 1, max_bet: 10000, options: [{ name: '', odds: 2 }, { name: '', odds: 2 }] };

export const AdminPredictionsPage = () => {
  const [predictions, setPredictions] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(null);
  const [winningOption, setWinningOption] = useState('');
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        adminAPI.getPredictions(),
        publicAPI.getCurrencies(true)
      ]);
      setPredictions(pRes.data);
      setCurrencies(cRes.data);
    } catch (error) {
      toast.error('Failed to load');
    } finally { setLoading(false); }
  };

  const addOption = () => setForm({...form, options: [...form.options, { name: '', odds: 2 }]});
  const removeOption = (idx) => setForm({...form, options: form.options.filter((_, i) => i !== idx)});
  const updateOption = (idx, field, value) => {
    const newOpts = [...form.options];
    newOpts[idx] = { ...newOpts[idx], [field]: field === 'odds' ? parseFloat(value) : value };
    setForm({...form, options: newOpts});
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (form.options.length < 2 || form.options.some(o => !o.name || o.odds <= 1)) {
      toast.error('At least 2 options with names and odds > 1.0');
      return;
    }
    try {
      await adminAPI.createPrediction({
        ...form,
        min_bet: parseFloat(form.min_bet),
        max_bet: parseFloat(form.max_bet),
        options: form.options.map(o => ({ name: o.name, odds: parseFloat(o.odds) }))
      });
      toast.success('Prediction created');
      setCreateOpen(false);
      setForm(emptyForm);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed');
    }
  };

  const openSettle = (pred) => {
    setSettleOpen(pred);
    setWinningOption(pred.options[0]?.name || '');
  };

  const submitSettle = async () => {
    if (!winningOption) { toast.error('Select winning option'); return; }
    try {
      const { data } = await adminAPI.settlePrediction(settleOpen.id, winningOption);
      toast.success(`Settled! ${data.winners} winners, paid ${data.total_paid.toFixed(2)}`);
      setSettleOpen(null);
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed');
    }
  };

  const closePred = async (id) => {
    if (!window.confirm('Close this prediction to new bets?')) return;
    try {
      await adminAPI.closePrediction(id);
      toast.success('Prediction closed');
      load();
    } catch (error) {
      toast.error('Failed');
    }
  };

  const deletePred = async (id) => {
    if (!window.confirm('Delete this prediction?')) return;
    try {
      await adminAPI.deletePrediction(id);
      toast.success('Deleted');
      load();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-predictions-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Trophy className="h-8 w-8 text-yellow-500" /> Predictions
            </h1>
            <p className="mt-2 text-muted-foreground">Create prediction games and settle results</p>
          </div>
          <Button onClick={() => { setForm(emptyForm); setCreateOpen(true); }} data-testid="create-prediction-button">
            <Plus className="h-4 w-4 mr-1" /> Create Prediction
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>All Predictions ({predictions.length})</CardTitle></CardHeader>
          <CardContent>
            {loading ? <p className="text-center py-8">Loading...</p> : predictions.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No predictions yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Options</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead>Bets</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {predictions.map(p => (
                    <TableRow key={p.id} data-testid={`admin-pred-${p.id}`}>
                      <TableCell className="font-medium max-w-xs truncate">{p.title}</TableCell>
                      <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                      <TableCell className="text-xs">{p.options.map(o => `${o.name}(${o.odds}x)`).join(', ')}</TableCell>
                      <TableCell>{p.currency}</TableCell>
                      <TableCell>{p.total_bets}</TableCell>
                      <TableCell>
                        <Badge className={p.status === 'open' ? 'bg-green-600' : p.status === 'settled' ? 'bg-blue-600' : 'bg-gray-500'}>
                          {p.status}
                          {p.winning_option && ` (${p.winning_option})`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        {p.status === 'open' && (
                          <Button size="sm" variant="outline" onClick={() => closePred(p.id)} title="Close to new bets">
                            <Lock className="h-3 w-3" />
                          </Button>
                        )}
                        {p.status !== 'settled' && (
                          <Button size="sm" variant="outline" className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => openSettle(p)} data-testid={`settle-${p.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Settle
                          </Button>
                        )}
                        {p.total_bets === 0 && (
                          <Button size="sm" variant="ghost" onClick={() => deletePred(p.id)}>
                            <Trash2 className="h-3 w-3 text-red-600" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Prediction</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required data-testid="pred-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="sports, politics..." />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({...form, currency: v})}>
                    <SelectTrigger data-testid="pred-currency-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Min Bet</Label>
                  <Input type="number" step="0.01" value={form.min_bet} onChange={(e) => setForm({...form, min_bet: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Max Bet</Label>
                  <Input type="number" step="0.01" value={form.max_bet} onChange={(e) => setForm({...form, max_bet: e.target.value})} required />
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Options with Odds</Label>
                  <Button type="button" size="sm" variant="ghost" onClick={addOption}>+ Add Option</Button>
                </div>
                {form.options.map((o, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      placeholder={`Option ${idx + 1} name`}
                      value={o.name}
                      onChange={(e) => updateOption(idx, 'name', e.target.value)}
                      required
                      data-testid={`pred-option-name-${idx}`}
                    />
                    <Input
                      type="number" step="0.01" min="1.01"
                      placeholder="Odds"
                      value={o.odds}
                      onChange={(e) => updateOption(idx, 'odds', e.target.value)}
                      required
                      className="w-24"
                      data-testid={`pred-option-odds-${idx}`}
                    />
                    {form.options.length > 2 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeOption(idx)}>
                        <Trash2 className="h-3 w-3 text-red-600" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-prediction-button">Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Settle Dialog */}
        <Dialog open={!!settleOpen} onOpenChange={(v) => !v && setSettleOpen(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Settle: {settleOpen?.title}</DialogTitle></DialogHeader>
            {settleOpen && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Select the winning option. Winners are auto-paid, losers forfeit their stake.</p>
                <div className="space-y-2">
                  <Label>Winning Option</Label>
                  <Select value={winningOption} onValueChange={setWinningOption}>
                    <SelectTrigger data-testid="winning-option-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {settleOpen.options.map(o => <SelectItem key={o.name} value={o.name}>{o.name} ({o.odds}x)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/30 border border-yellow-200 text-sm text-yellow-800 dark:text-yellow-300">
                  ⚠️ This will settle {settleOpen.total_bets} bets. Cannot be undone.
                </div>
                <Button className="w-full" onClick={submitSettle} data-testid="confirm-settle-button">
                  Settle & Pay Winners
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};
